import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PriseServiceDialogComponent } from '@common/dialog/prise-service-dialog/prise-service-dialog.component';
import {
  UserProfile,
  UserProfileDetailed,
} from '@common/model/user-profile.model';
import { environment } from '@environments/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DelegationInfo, DelegationState, DelegationSwitchRequest, DelegationTokenResponse } from '@shared/model/delegation.model';
import { KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, catchError, Observable, of, Subject, tap, throwError } from 'rxjs';
import { DelegationTokenService } from './delegation-token.service';
import {ThemeService} from "@core/service/theme.service";
import {ThemeReferentiel} from "@common/model/theme-ref.enum";

const ROLE_ADMIN = 'ROLE_SUPER';

export type EntityArrayResponseType = HttpResponse<any[]>;
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private resourceUrl = `${environment.SERVER_UTILISATEUR_API_URL}/api/account`;

  private userInfo$ = new BehaviorSubject<UserProfile | undefined>(undefined);
  private authInitialized$ = new BehaviorSubject<boolean>(false);

  private doLogOut = false;

  // Delegation state
  private isDelegating$ = new BehaviorSubject<boolean>(false);
  private delegationState$ = new BehaviorSubject<DelegationState | null>(null);

  private originalUserProfile: UserProfile | null = null;

  constructor(
    private keycloakService: KeycloakService,
    protected http: HttpClient,
    private modalService: NgbModal,
    private delegationTokenService: DelegationTokenService,
    private themeService: ThemeService
  ) {
    this.handleAuthEvents();
    this.loadUserInfo(keycloakService.isLoggedIn());
    this.initializeDelegationState();

  }

  private handleAuthEvents() {
    this.keycloakService.keycloakEvents$.subscribe({
      next: (event) => {
        if (
          !this.isAuthenticated() &&
          event.type == KeycloakEventType.OnAuthSuccess
        ) {
          this.loadUserInfo(true);
        } else if (
          this.isAuthenticated() &&
          event.type == KeycloakEventType.OnAuthLogout
        ) {
          this.loadUserInfo(false);
        } else if (
          event.type === KeycloakEventType.OnAuthRefreshError ||
          event.type === KeycloakEventType.OnTokenExpired
        ) {
          this.doLogOut = true;
          setTimeout(() => {
            if (this.doLogOut) {
              this.loadUserInfo(false);
              this.logout();
            }
          }, 300000);
        } else if (event.type === KeycloakEventType.OnAuthRefreshSuccess) {
          this.doLogOut = false;
        }
      },
    });
  }

  private loadUserInfo(authenticated: boolean) {
    if (authenticated) {
      // this.userInfo$.next({
      //   userName: 'super',
      //   userFullName: 'admin test',
      //   lang: 'fr',
      //   userFullNameAr: 'admin test',
      //   userRoles: ['ROLE_SUPER'],
      //   userRights: [],
      // });
      this.loadProfile().subscribe({
        next: (up: UserProfile) => {
          up.roles = (up.roles ?? []);
          up.ressources = up.ressources ?? [];
          this.userInfo$.next(up);
          this.authInitialized$.next(true);
          this.themeService.setTheme(up.theme);
        },
      });
    } else {
      this.userInfo$.next(undefined);
      this.authInitialized$.next(true);
      this.themeService.setTheme(ThemeReferentiel.Default);
    }
  }

  getRedirectUri() {
    let port = location.port ? ':' + location.port : '';
    return location.protocol + '//' + location.hostname + port + '/';
  }

  logout() {
    this.userInfo$.next(undefined);
    this.keycloakService.logout(this.getRedirectUri());
  }

  profile() {
    this.keycloakService.getKeycloakInstance().accountManagement();
  }

  changePassword(): void {
    this.keycloakService.login({
      action: 'UPDATE_PASSWORD',
      redirectUri: this.getRedirectUri(),
    });
  }

  getRoles() {
    if (!this.isAuthenticated()) {
      return [];
    }
    return this.keycloakService.getUserRoles();
  }

  getProfile(): Observable<UserProfile | undefined> {
    return this.userInfo$.asObservable();
  }

  afterInit(): Observable<boolean> {
    return this.authInitialized$.asObservable();
  }

  isAuthenticated() {
    return this.keycloakService.isLoggedIn();
  }

  isSuperAdmin() {
    if (!this.isAuthenticated()) {
      return false;
    }
    return this.isUserInRole(ROLE_ADMIN);
  }

  isUserInRole(role: string) {
    let up = this.getProfileSync();
    if (!up) {
      return false;
    }
    return up.roles.includes(role);
  }

  getProfileSync(): UserProfile | undefined {
    return this.userInfo$.getValue();
  }

  hasAnyRole(authorities: string[]): boolean {
    if (!this.getProfileSync()) {
      return false;
    }
    if (this.isSuperAdmin()) {
      return true;
    }
    return authorities.some((role) => this.isUserInRole(role));
  }

  hasAnyAccess(accesses: string[]): boolean {
    //var access =  accesses.reduce((a, v) => ({ ...a, ['appRightName']: v}), {});
    let up = this.getProfileSync();
    if (!up) {
      return false;
    }
    if (this.isSuperAdmin()) {
      return true;
    }
    return accesses.some((access) => up?.ressources.includes(access));
  }

  loadProfile(): Observable<UserProfile> {

    return this.http.get<any>(
      `${this.resourceUrl}`,
      { observe: 'body' }
    );

    // return this.http.get<UserProfile>(
    //   `${this.resourceUrl}/CurrentUser/Identity`,
    //   { observe: 'body' }
    // );
    // return this.http.get<UserProfile>(`${this.resourceUrl}/CurrentUser/POSIdentity`, {observe: 'body'});
  }

  getUserProfiles(): Observable<UserProfileDetailed[]> {
    return this.http.get<UserProfileDetailed[]>(`${this.resourceUrl}`, {
      observe: 'body',
    });
  }

  // Initialize delegation state from localStorage
  private initializeDelegationState() {
    const delegationStateStr = localStorage.getItem('delegationState');
    const originalProfileStr = localStorage.getItem('originalUserProfile');

    if (delegationStateStr) {
      try {
        const delegationState: DelegationState = JSON.parse(delegationStateStr);

        // Check if delegation hasn't expired
        const now = new Date();
        const expiresAt = new Date(delegationState.expiresAt);

        if (expiresAt > now) {
          this.delegationTokenService.setDelegationId(String(delegationState.delegationId));
          this.delegationState$.next(delegationState);
          this.isDelegating$.next(true);

          if (originalProfileStr) {
            this.originalUserProfile = JSON.parse(originalProfileStr);
          }

          // Load delegated profile with fresh data
          this.loadDelegatedUserProfile();
        } else {
          // Delegation expired, clean up
          this.clearDelegationState();
        }
      } catch (error) {
        console.error('Error restoring delegation state:', error);
        this.clearDelegationState();
      }
    }
  }

  // Clear delegation state
  private clearDelegationState() {
    this.delegationTokenService.clearDelegation();
    this.delegationState$.next(null);
    this.isDelegating$.next(false);

    localStorage.removeItem('delegationToken');
    localStorage.removeItem('delegationState');
    localStorage.removeItem('originalUserProfile');
  }

  // Charge le profil du delegant : DelegationInterceptor ajoute l'en-tete X-Delegation,
  // le backend renvoie alors les roles et habilitations du delegant.
  private loadDelegatedUserProfile() {
    if (!this.delegationTokenService.getDelegationId()) return;

    this.http.get<UserProfile>(`${this.resourceUrl}`).subscribe({
      next: (profile) => {
        // Get delegation state from memory (not from JWT)
        const delegationState = this.delegationState$.getValue();

        // Enhance profile with delegation context
        profile.isDelegating = true;
        profile.currentDelegation = delegationState;

        // All user data (roles, permissions, name) comes fresh from API
        this.userInfo$.next(profile);
        this.themeService.setTheme(profile.theme );

      },
      error: (error) => {
        // If delegation token is invalid, switch back
        this.switchBackToOriginal().subscribe();
      }
    });
  }

  // Switch back to original user (frontend only)
  switchBackToOriginal(): Observable<void> {
    return new Observable(observer => {
      try {
        this.clearDelegationState();

        // Restore original profile
        if (this.originalUserProfile) {
          this.userInfo$.next(this.originalUserProfile);
          this.themeService.setTheme(this.originalUserProfile.theme );

          this.originalUserProfile = null;
        } else {
          // Reload from server if original profile not available
          this.loadUserInfo(true);
        }

        observer.next();
        observer.complete();
      } catch (error) {
        observer.error(error);
      }
    });
  }

   // Switch to delegation mode
  switchToDelegation(delegationId: string): Observable<DelegationTokenResponse> {
    const request: DelegationSwitchRequest = { delegationId };

    return this.http.post<DelegationTokenResponse>(
      `${this.resourceUrl}/delegation/switch`,
      request
    ).pipe(
      tap(response => {
        this.handleDelegationSwitch(response);
      }),
      catchError(error => {
        console.error('Error switching to delegation:', error);
        return throwError(() => error);
      })
    );
  }

  // Handle successful delegation switch
  private handleDelegationSwitch(response: DelegationTokenResponse) {
    // Store original profile if not already stored
    if (!this.originalUserProfile) {
      this.originalUserProfile = this.getProfileSync() || null;
      if (this.originalUserProfile) {
        localStorage.setItem('originalUserProfile', JSON.stringify(this.originalUserProfile));
      }
    }

    // Create delegation state from API response (not from JWT)
    const delegationState: DelegationState = {
      isDelegating: true,
      delegationId: response.delegationId,
      delegatorName: response.delegatorName,  // From API response
      originalUser: this.originalUserProfile?.login || '',
      expiresAt: new Date(response.expiresAt)
    };

    // Store in memory and localStorage (le backend n'emet pas de jeton : la delegation
    // est portee par son id, envoye dans l'en-tete X-Delegation)
    this.delegationState$.next(delegationState);
    this.isDelegating$.next(true);

    localStorage.setItem('delegationState', JSON.stringify(delegationState));

    this.delegationTokenService.setDelegationId(String(response.delegationId));


    // Load fresh delegated user profile
    this.loadDelegatedUserProfile();
  }

  createDelegation(
    delegataireLogin: string,
    dateDebut: Date,
    dateFin: Date,
    delegationReason: string
  ): Observable<DelegationInfo> {
      const createDelegationDTO = {
        delegataireLogin,
        dateDebut: dateDebut.toISOString(),
        dateFin: dateFin.toISOString(),
        delegationReason
      };

      return this.http.post<DelegationInfo>(
        `${this.resourceUrl}/delegations`,
        createDelegationDTO
      ).pipe(
        catchError(error => {
          console.error('Error creating delegation:', error);
          return throwError(() => error);
        })
      );
  }

  


  isDelegatingSync(): boolean {
    return this.isDelegating$.getValue();
  }

  getDelegationId(): string | null {
    return this.delegationTokenService.getDelegationId();
  }

  getDelegationState(): Observable<DelegationState | null> {
    return this.delegationState$.asObservable();
  }

  getIsDelegating(): Observable<boolean> {
    return this.isDelegating$.asObservable();
  }

  // Get available delegations for current user
  getAvailableDelegations(): Observable<DelegationInfo[]> {
    return this.http.get<DelegationInfo[]>(`${this.resourceUrl}/delegations/available`)
      .pipe(
        catchError(error => {
          console.error('Error fetching available delegations:', error);
          return of([]);
        })
      );
  }

  // Get delegations created by current user (where current user is delegator)
  getCreatedDelegations(): Observable<DelegationInfo[]> {
    return this.http.get<DelegationInfo[]>(`${this.resourceUrl}/delegations/created`)
      .pipe(
        catchError(error => {
          console.error('Error fetching created delegations:', error);
          return of([]);
        })
      );
  }

  updateUserTheme(newTheme: ThemeReferentiel): void { // Correction ici
    const currentUser = this.userInfo$.getValue();
    if (currentUser) {
      const updatedUser = { ...currentUser, theme: newTheme };
      this.userInfo$.next(updatedUser); // Mettre à jour l'état local de l'utilisateur
      this.themeService.setTheme(newTheme); // Appliquer le nouveau thème via le service de thème
    }
  }

}
